import React, { useCallback, useMemo } from 'react';
// import * as Application from 'expo-application';
import { useDispatch } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { DrawerContentScrollView } from '@react-navigation/drawer';

import {
	View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { logoutSucceeded } from '../../../common/components/Auth/authSlice';
import { getUserName } from '../../../common/utils/user/getUserName';
import { useUserContext } from '../../../common/utils/context';
import useMyDayMode, { MY_DAY_MODE } from '../../scenes/myDay/useMyDayMode';
import { DRIVER_ROUTE } from '../../scenes/driver/driverRoutes';
import { isLegacyMobileUi } from '../../utils/legacyMobileUi';
import { getMenuAccess } from '../../utils/menuAccess';

import { useThemedStyles } from '../../hooks/useThemedStyles';

import NavigationMenuItem from './NavigationMenuItem';
import goToShellRoute, { getMainStackState } from './goToShellRoute';
import { Text, UserAvatar } from '../index';


const NavigationMenu = (props) => {
	const { navigation, state: drawerState } = props;
	const dispatch = useDispatch();
	const client = useApolloClient();
	const styles = useThemedStyles(createStyles);
	const {
		user,
		isAVC,
		isDriver,
		isPicker,
		canCompleteAssignedMarketSetups,
		canViewClientsLeads,
	} = useUserContext();
	const { canAccessMyDayFeature: canViewMyDay, ticketScope, mode } = useMyDayMode();
	const isLegacyUi = isLegacyMobileUi(user);
	const insets = useSafeAreaInsets();
	const userName = getUserName(user);
	const drawerContentStyle = useMemo(() => {
		return [
			styles.scrollContainer,
			{
				paddingBottom: Math.max(insets.bottom, 16),
			},
		];
	}, [insets.bottom, styles.scrollContainer]);
	const mainStack = getMainStackState(drawerState);

	// Redesigned app: My day is Home and Service runs is All Runs, opened like
	// their tabs. Legacy UI: My day users have the board as Home, so the classic
	// run list is its own screen for them; everyone else keeps Home (which is the run list).
	const goToServiceRuns = useCallback(() => {
		if (!isLegacyUi) {
			goToShellRoute(navigation, mainStack, DRIVER_ROUTE.ALL_RUNS);
			navigation.closeDrawer();
			return;
		}
		navigation.navigate('MainStack', { screen: canViewMyDay ? 'ServiceRunListView' : 'Home' });
	}, [canViewMyDay, isLegacyUi, mainStack, navigation]);

	const goToMyDay = useCallback(() => {
		if (!isLegacyUi) {
			goToShellRoute(navigation, mainStack, DRIVER_ROUTE.HOME);
			navigation.closeDrawer();
			return;
		}
		navigation.navigate('MainStack', { screen: 'MyDayView' });
	}, [isLegacyUi, mainStack, navigation]);

	const goToServiceRunCreator = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'ServiceRunCreatorView' });
	}, [navigation]);

	const goToSettings = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'SettingsView' });
	}, [navigation]);

	const goToMyAccount = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'MyAccountView' });
	}, [navigation]);
	const goToWarehouse = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'WarehouseView' });
	}, [navigation]);
	const goToClockPass = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'ClockPassView' });
	}, [navigation]);
	const goToProcedures = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'ProcedureListView' });
	}, [navigation]);

	const goToTrainings = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'TrainingListView' });
	}, [navigation]);
	const goToLeads = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'LeadListView' });
	}, [navigation]);

	const goToTickets = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'TicketListView' });
	}, [navigation]);

	const goToApprovals = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'ChecklistApprovalsView' });
	}, [navigation]);

	const goToNotifications = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'ChecklistNotificationsView' });
	}, [navigation]);

	const goToSetups = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'SetupsView' });
	}, [navigation]);

	const goToPointOfSales = useCallback(() => {
		navigation.navigate('MainStack', { screen: 'PointOfSaleListView' });
	}, [navigation]);

	const logoutOfApp = useCallback(async () => {
		await AsyncStorage.removeItem('token');
		client.resetStore();
		dispatch(logoutSucceeded());
		// Navigation will be handled automatically by AppNavigation
		// based on the Redux auth state change
	}, [client, dispatch]);

	const {
		canViewServiceRuns,
		canAddServiceRuns,
		canViewWarehouseMenu,
		canViewTicketList,
		canApproveChecklists,
		canViewPointsOfSale,
		canViewSetups,
		canViewClockPass,
		canViewProcedures,
		canViewTrainings,
	} = getMenuAccess({
		user,
		isAVC,
		isDriver,
		isPicker,
		canCompleteAssignedMarketSetups,
		ticketScope,
	});

	const items = [
		{ key: 'myDay', label: 'My day', icon: 'sun', onPress: goToMyDay, visible: canViewMyDay },
		// Profile and My roster moved inside My account, mirroring the People app.
		{ key: 'myAccount', label: 'My account', icon: 'user', onPress: goToMyAccount, visible: true },
		{ key: 'clockPass', label: 'Shift pass', icon: 'grid', onPress: goToClockPass, visible: canViewClockPass },
		{ key: 'procedures', label: 'Procedures', icon: 'file-text', onPress: goToProcedures, visible: canViewProcedures },
		{ key: 'trainings', label: 'Training', icon: 'book-open', onPress: goToTrainings, visible: canViewTrainings },
		{ key: 'leads', label: 'Leads', icon: 'users', onPress: goToLeads, visible: Boolean(canViewClientsLeads) },
		// Same name as the tab it opens; the legacy UI keeps its classic run list name.
		{ key: 'serviceRuns', label: isLegacyUi ? 'Service runs' : isDriver && mode !== MY_DAY_MODE.PICKER ? 'My runs' : 'All Runs', icon: 'truck', onPress: goToServiceRuns, visible: canViewServiceRuns },
		{ key: 'tickets', label: 'Tickets', icon: 'tag', onPress: goToTickets, visible: canViewTicketList },
		{ key: 'approvals', label: 'Approvals', icon: 'check-square', onPress: goToApprovals, visible: canApproveChecklists },
		{ key: 'notifications', label: 'Notifications', icon: 'bell', onPress: goToNotifications, visible: canApproveChecklists },
		{ key: 'pointsOfSale', label: 'Points of sale', icon: 'shopping-cart', onPress: goToPointOfSales, visible: canViewPointsOfSale },
		{ key: 'setups', label: 'Setups', icon: 'layers', onPress: goToSetups, visible: canViewSetups },
		{ key: 'planServiceRun', label: 'Plan service run', icon: 'calendar', onPress: goToServiceRunCreator, visible: canAddServiceRuns },
		{ key: 'warehouse', label: 'Warehouse', icon: 'package', onPress: goToWarehouse, visible: canViewWarehouseMenu },
		{ key: 'settings', label: 'Settings', icon: 'settings', onPress: goToSettings, visible: true },
	].filter(item => item.visible);

	return (
		<View style={[styles.menuContainer, { paddingTop: insets.top }]}>
			<View style={styles.profileSummary}>
				<UserAvatar
					name={userName}
					photoUrl={user?.profilePhotoUrl}
					size={60}
					style={styles.avatar}
					initialsStyle={styles.avatarInitials}
				/>
				<View style={styles.profileTextGroup}>
					<Text style={styles.profileName} numberOfLines={1}>{userName}</Text>
					{
						Boolean(user?.username) && (
							<Text style={styles.profileMeta} numberOfLines={1}>{user.username}</Text>
						)
					}
					{
						Boolean(user?.role?.name) && (
							<Text style={styles.profileRole} numberOfLines={1}>{user.role.name}</Text>
						)
					}
				</View>
			</View>
			<DrawerContentScrollView
				{...props}
				contentContainerStyle={drawerContentStyle}
				showsVerticalScrollIndicator={false}
			>
				{
					items.map(item => (
						<NavigationMenuItem
							key={item.key}
							label={item.label}
							icon={item.icon}
							onPress={item.onPress}
						/>
					))
				}
				<NavigationMenuItem
					label="Logout"
					icon="log-out"
					onPress={logoutOfApp}
					isDanger={true}
					isLast={true}
				/>
			</DrawerContentScrollView>

			<View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, 12) }]}>
				{/* <Text style={styles.appVersion}>
					v. {Application.nativeApplicationVersion || 'Unknown'}
				</Text> */}
				<Text style={styles.appVersion}>
					Sentry · v. 2.105.25
				</Text>
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	menuContainer: {
		flexDirection: 'column',
		flex: 1,
		backgroundColor: colors.surface,
	},
	scrollContainer: {
		paddingTop: 0,
		paddingStart: 0,
		paddingEnd: 0,
	},
	profileSummary: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 14,
		paddingTop: 22,
		paddingHorizontal: space.gutter,
		paddingBottom: space.gutter,
		borderBottomWidth: 1,
		borderBottomColor: colors.border,
	},
	// The drawer avatar has no ring and uses the card title type for initials.
	avatar: {
		borderWidth: 0,
	},
	avatarInitials: {
		...type.cardTitle,
		letterSpacing: 0,
	},
	profileTextGroup: {
		flex: 1,
		minWidth: 0,
		gap: space.xxs,
	},
	profileName: {
		...type.sectionTitle,
		color: colors.text,
	},
	profileMeta: {
		...type.caption,
		color: colors.textSecondary,
	},
	profileRole: {
		...type.overline,
		color: colors.accent,
	},
	footer: {
		alignItems: 'center',
		borderTopWidth: 1,
		borderTopColor: colors.border,
		paddingTop: space.md,
	},
	appVersion: {
		...type.caption,
		color: colors.textSecondary,
	},
});

export default NavigationMenu;
